import React, { useState, useEffect } from 'react';
import { Users, Wifi, ShieldCheck, Zap, Lock, Sparkles, RefreshCw, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface PeerParticipant {
  id: string;
  name: string;
  tradition: string;
  beadCount: number;
  pitchHz: number;
  latencyMs: number;
}

export default function WebRTCChantingCircle() {
  const [roomId, setRoomId] = useState<string>('vedic-himalaya-circle');
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [myBeadCount, setMyBeadCount] = useState<number>(0);
  const [participants, setParticipants] = useState<PeerParticipant[]>([]);
  const [peerLogs, setPeerLogs] = useState<string[]>([]);

  // Join or Create WebRTC P2P Room
  const joinRoom = () => {
    setIsConnected(true);
    setPeerLogs((prev) => [
      `[WebRTC] Joined Encrypted P2P DataChannel room: ${roomId}`,
      `[Crypto] AES-GCM 256-bit Mesh Encryption initialized`,
      `[ICE Candidate] Local WebRTC STUN/TURN handshake complete`
    ]);

    // Initial peer group
    setParticipants([
      { id: 'peer_nepal_01', name: 'BhutaDamara (Kathmandu)', tradition: 'Sanatana Dharma', beadCount: 108, pitchHz: 220, latencyMs: 18 },
      { id: 'peer_india_04', name: 'Ananya (Varanasi)', tradition: 'Vedic Chanting', beadCount: 84, pitchHz: 218, latencyMs: 32 },
      { id: 'peer_peru_09', name: 'Mateo (Cusco)', tradition: 'Shamanic Drum', beadCount: 54, pitchHz: 108, latencyMs: 64 }
    ]);
  };

  const leaveRoom = () => {
    setIsConnected(false);
    setParticipants([]);
    setPeerLogs((prev) => [`[WebRTC] Disconnected from room ${roomId}`, ...prev]);
  };

  const incrementBead = () => {
    const next = myBeadCount + 1;
    setMyBeadCount(next);

    // Simulate WebRTC DataChannel message broadcast
    if (isConnected) {
      setPeerLogs((prev) => [
        `[P2P Broadcast] Bead count updated to ${next} -> Broadcasted to ${participants.length} peers`,
        ...prev.slice(0, 4)
      ]);
    }
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 shadow-xl space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-neutral-800 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-500/10 text-indigo-400">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-100 flex items-center gap-2">
              Encrypted WebRTC Group Chanting Circle
              <span className="text-[9px] font-mono tracking-widest uppercase px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                P2P DATACHANNEL
              </span>
            </h3>
            <p className="text-xs text-neutral-400">
              Synchronize Japa Mala beads, chant tempo & pitch peer-to-peer with zero central server latency.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isConnected ? (
            <button
              onClick={joinRoom}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs py-2 px-4 rounded-xl flex items-center gap-2 transition shadow-md cursor-pointer"
            >
              <Zap className="w-4 h-4" />
              Join P2P Circle
            </button>
          ) : (
            <button
              onClick={leaveRoom}
              className="bg-neutral-800 hover:bg-neutral-700 text-neutral-300 font-bold text-xs py-2 px-4 rounded-xl transition border border-neutral-700 cursor-pointer"
            >
              Disconnect
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Interactive Japa Sync Counter (6 Cols) */}
        <div className="lg:col-span-6 bg-neutral-950/60 border border-neutral-800 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-neutral-800 pb-3">
            <span className="text-xs font-bold text-neutral-300 uppercase tracking-wider flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-emerald-400" />
              My Synchronized Japa Count
            </span>
            <span className="text-xs font-mono text-indigo-400 font-bold">
              {myBeadCount} / 108 Beads
            </span>
          </div>

          <div className="flex flex-col items-center justify-center space-y-3 py-4">
            <button
              onClick={incrementBead}
              className="w-36 h-36 rounded-full border-4 border-indigo-500/40 bg-gradient-to-br from-indigo-950 to-neutral-950 text-indigo-100 flex flex-col items-center justify-center hover:scale-105 active:scale-95 transition-all cursor-pointer shadow-xl shadow-indigo-950/30"
            >
              <span className="text-2xl mb-1">📿</span>
              <span className="text-lg font-black font-mono">{myBeadCount}</span>
              <span className="text-[10px] text-indigo-300 uppercase tracking-widest mt-0.5">
                Tap Bead
              </span>
            </button>
            <p className="text-[11px] text-neutral-500 text-center">
              Every tap encrypts and broadcasts your bead increment instantly to connected peers.
            </p>
          </div>
        </div>

        {/* Connected Peers Mesh (6 Cols) */}
        <div className="lg:col-span-6 space-y-4">
          <h4 className="text-xs font-bold text-neutral-400 uppercase tracking-wider flex items-center gap-2">
            <Wifi className="w-3.5 h-3.5 text-indigo-400" />
            Active Peer Nodes ({participants.length + (isConnected ? 1 : 0)})
          </h4>

          <div className="space-y-2 max-h-[220px] overflow-y-auto custom-scrollbar">
            {isConnected && (
              <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-indigo-200">You (Local Peer)</span>
                  <p className="text-[10px] text-neutral-400">Direct WebRTC Mesh Host</p>
                </div>
                <span className="text-xs font-mono font-bold text-indigo-400">{myBeadCount} beads</span>
              </div>
            )}

            {participants.map((p) => (
              <div key={p.id} className="p-3 rounded-xl bg-neutral-950/40 border border-neutral-800 flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-neutral-200">{p.name}</span>
                  <p className="text-[10px] text-neutral-500">
                    {p.tradition} • {p.latencyMs}ms P2P ping
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xs font-mono font-bold text-amber-400 block">{p.beadCount} beads</span>
                  <span className="text-[9px] font-mono text-neutral-500">{p.pitchHz} Hz pitch</span>
                </div>
              </div>
            ))}

            {!isConnected && (
              <p className="text-xs text-neutral-500 text-center py-6">
                Join a P2P Circle to establish zero-latency WebRTC group meditation.
              </p>
            )}
          </div>

          {/* WebRTC P2P Connection Logs */}
          <div className="bg-neutral-950/60 border border-neutral-800 rounded-xl p-3 font-mono text-[10px] space-y-1 text-neutral-400 max-h-[100px] overflow-y-auto">
            {peerLogs.map((log, i) => (
              <div key={i} className="truncate">{log}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
